/**
 * Renderiza un bloque de blog con edición visual.
 */

import type { BlogBlock } from './types';
import { EditableText } from './EditableText';
import { BlockToolbar } from './BlockToolbar';
import { ImageIcon } from 'lucide-react';

interface BlogBlockRendererProps {
    block: BlogBlock;
    isFirst: boolean;
    isLast: boolean;
    onUpdate: (updates: Partial<BlogBlock>) => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onRemove: () => void;
    onDuplicate: () => void;
    onImagePick: (blockId: string) => void;
    resolveImageUrl: (url: string) => string;
}

export function BlogBlockRenderer({
    block,
    isFirst,
    isLast,
    onUpdate,
    onMoveUp,
    onMoveDown,
    onRemove,
    onDuplicate,
    onImagePick,
    resolveImageUrl,
}: BlogBlockRendererProps) {
    const blockClasses =
        'relative group/block rounded-lg border border-transparent hover:border-border/50 transition-colors py-1 px-2 -mx-2';

    switch (block.type) {
        case 'heading': {
            const Tag = `h${block.level}` as 'h1' | 'h2' | 'h3';
            const sizeClass =
                block.level === 1
                    ? 'text-2xl'
                    : block.level === 2
                      ? 'text-xl'
                      : 'text-lg';
            return (
                <div className={blockClasses}>
                    <BlockToolbar
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                        onRemove={onRemove}
                        onDuplicate={onDuplicate}
                        isFirst={isFirst}
                        isLast={isLast}
                    />
                    <EditableText
                        tag={Tag}
                        value={block.content}
                        onChange={(v) => onUpdate({ content: v })}
                        className={`font-bold ${sizeClass} block`}
                        placeholder={`Título ${block.level}...`}
                    />
                </div>
            );
        }

        case 'paragraph':
            return (
                <div className={blockClasses}>
                    <BlockToolbar
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                        onRemove={onRemove}
                        onDuplicate={onDuplicate}
                        isFirst={isFirst}
                        isLast={isLast}
                    />
                    <EditableText
                        tag="p"
                        value={block.content}
                        onChange={(v) => onUpdate({ content: v })}
                        className="text-base leading-relaxed"
                        placeholder="Escribe un párrafo..."
                        richFormatting
                    />
                </div>
            );

        case 'image':
            return (
                <div className={blockClasses}>
                    <BlockToolbar
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                        onRemove={onRemove}
                        onDuplicate={onDuplicate}
                        isFirst={isFirst}
                        isLast={isLast}
                    />
                    <div
                        className="relative rounded-lg overflow-hidden bg-muted min-h-[120px] cursor-pointer"
                        onClick={() => onImagePick(block.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                            e.key === 'Enter' && onImagePick(block.id)
                        }
                    >
                        {block.url ? (
                            <img
                                src={resolveImageUrl(block.url)}
                                alt={block.alt ?? ''}
                                className="w-full max-w-md h-auto object-cover rounded-lg"
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                                <ImageIcon className="w-10 h-10 mb-2" />
                                <span className="text-sm">
                                    Clic para seleccionar imagen
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            );

        case 'list':
            return (
                <div className={blockClasses}>
                    <BlockToolbar
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                        onRemove={onRemove}
                        onDuplicate={onDuplicate}
                        isFirst={isFirst}
                        isLast={isLast}
                    />
                    <div
                        className="space-y-1"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(block.listType === 'ordered' ? (
                            <ol className="list-decimal list-inside space-y-1">
                                {block.items.map((item, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="text-muted-foreground shrink-0">
                                            {i + 1}.
                                        </span>
                                        <EditableText
                                            tag="span"
                                            value={item}
                                            onChange={(v) => {
                                                const items = [...block.items];
                                                items[i] = v;
                                                onUpdate({ items });
                                            }}
                                            className="flex-1"
                                            placeholder="Elemento..."
                                            richFormatting
                                        />
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <ul className="list-disc list-inside space-y-1">
                                {block.items.map((item, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="text-muted-foreground shrink-0">
                                            •
                                        </span>
                                        <EditableText
                                            tag="span"
                                            value={item}
                                            onChange={(v) => {
                                                const items = [...block.items];
                                                items[i] = v;
                                                onUpdate({ items });
                                            }}
                                            className="flex-1"
                                            placeholder="Elemento..."
                                            richFormatting
                                        />
                                    </li>
                                ))}
                            </ul>
                        ))}
                        <button
                            type="button"
                            onClick={() =>
                                onUpdate({
                                    items: [...block.items, ''],
                                })
                            }
                            className="text-xs text-muted-foreground hover:text-foreground"
                        >
                            + Añadir elemento
                        </button>
                    </div>
                </div>
            );

        case 'blockquote':
            return (
                <div className={blockClasses}>
                    <BlockToolbar
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                        onRemove={onRemove}
                        onDuplicate={onDuplicate}
                        isFirst={isFirst}
                        isLast={isLast}
                    />
                    <blockquote className="border-l-4 border-primary/50 pl-4 italic text-muted-foreground">
                        <EditableText
                            tag="p"
                            value={block.content}
                            onChange={(v) => onUpdate({ content: v })}
                            className="text-base"
                            placeholder="Cita..."
                            richFormatting
                        />
                    </blockquote>
                </div>
            );

        default:
            return null;
    }
}
